/* ---------------------------------------------------------------------------
   HCS shared analytics — 25 Jul 2026
   ---------------------------------------------------------------------------
   Why this file exists
   -------------------
   Until now the ONLY instrumented surface on the storefront was the mobile
   sticky add-to-cart bar. The mega menu (desktop), the drawer menu (mobile)
   and the collection / subcategory grids had no measurement at all, so we
   could see that someone landed on a subcategory page but not how they got
   there, which categories get opened and abandoned, or whether anyone clicks
   past the first row of a grid.

   Transport
   ---------
   The storefront loads the Google tag GT-5M3JQLVQ, which fans out to GA4
   G-H0WT0VVER1 and MC-PYVH4S4T2R. There is NO GTM container on the theme
   (GTM-K864PDT7 lives only inside the Shopify Customer Events pixel sandbox),
   so gtag() reaches GA4 directly and window.dataLayer.push() reaches nothing
   from theme code. We call gtag() and keep the dataLayer push only in case a
   container is ever added to the theme.

   IMPORTANT: every parameter below must be registered in GA4 as an
   event-scoped custom dimension or it is collected but not reportable, and
   registration does NOT backfill. Registered 25 Jul 2026.

   Events
   ------
     hcs_nav_open         a top-level menu was opened
     hcs_nav_click        any nav link clicked (L1/L2/L3, position, surface)
     hcs_collection_view  collection/subcategory page loaded (+ product count)
     hcs_card_click       product card clicked in a grid (+ position, stock)
     hcs_facet_apply      filter or sort used
     hcs_collection_scroll  25/50/75/100% depth milestones
     hcs_collection_empty   zero results (a dead end)

   Everything here is passive measurement. No behaviour, layout or navigation
   is changed: no preventDefault, no stopPropagation, all listeners passive
   where possible, every handler wrapped so a throw can never break the page.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (window.__hcsTrackLoaded) return;
  window.__hcsTrackLoaded = true;

  /* --- transport ---------------------------------------------------------- */

  function viewport() {
    return window.innerWidth < 750 ? 'mobile' : 'desktop';
  }

  /* Canonical site-wide tracker. The sticky ATC snippet wraps this to stamp
     product_handle on product pages; it falls back to its own inline copy if
     this asset ever fails to load, so the two are independent. */
  /* Which of the three navigation layouts the visitor is looking at. UI-8/9/10
     replaced one desktop mega menu with two distinct desktop surfaces, so
     'desktop' alone no longer says what someone actually saw:
       expanded  — the full header: eleven collections across the grey bar
       collapsed — scrolled: a single SHOP dropdown with flyouts
       mobile    — the drawer, unchanged by any of this
     Registered in GA4 as an event-scoped custom dimension. */
  function navView() {
    try {
      if (window.innerWidth < 990) return 'mobile';
      var section = document.querySelector('.section-header');
      if (section && section.classList.contains('scrolled-past-header')) return 'collapsed';
      return 'expanded';
    } catch (e) {
      return 'unknown';
    }
  }

  /* Bumped whenever the navigation itself changes shape, so GA4 and Clarity can
     separate before/after without relying on someone remembering the date.
     See CHANGELOG.md. */
  var NAV_VERSION = 'ui10';

  /* Clarity loads via the Shopify app and may not be ready when this runs, so
     the tags are queued and retried briefly. Tags are session-scoped and become
     filters on heatmaps and recordings. */
  function clarityTag(key, value) {
    var tries = 0;
    (function attempt() {
      try {
        if (typeof window.clarity === 'function') {
          window.clarity('set', key, String(value));
          return;
        }
      } catch (e) {}
      if (++tries < 20) window.setTimeout(attempt, 500);
    })();
  }

  function hcsTrackEvent(event, params) {
    var g = {};
    try {
      for (var k in params) {
        if (Object.prototype.hasOwnProperty.call(params, k)) g[k] = params[k];
      }
      if (!g.viewport) g.viewport = viewport();
      if (!g.nav_view) g.nav_view = navView();
      if (!g.nav_version) g.nav_version = NAV_VERSION;
    } catch (e) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', event, g);
    } catch (e) {}
    try {
      /* Build a fresh object: GTM stamps a `gtm` key onto anything pushed to
         dataLayer, and reusing the same object sent junk params to GA4 once
         before (see hcs-sticky-atc.liquid, commit 5b559c2). */
      var payload = { event: event };
      for (var k2 in g) {
        if (Object.prototype.hasOwnProperty.call(g, k2)) payload[k2] = g[k2];
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
  }
  window.hcsTrackEvent = hcsTrackEvent;
  window.hcsClarityTag = clarityTag;

  /* every session gets the nav version; the view is tagged on first nav use,
     because a session can cross both desktop layouts by scrolling */
  clarityTag('nav_version', NAV_VERSION);

  /* Wrap a handler so a throw inside it can never break the page. */
  function safe(fn) {
    return function (e) {
      try {
        fn(e);
      } catch (err) {
        try {
          console.debug('[hcs-track]', err);
        } catch (e2) {}
      }
    };
  }

  /* --- helpers ------------------------------------------------------------ */

  /* Visible label of an element, with icon markup stripped. Menu summaries
     contain inline <svg> carets which would otherwise pollute the label. */
  function labelOf(el) {
    if (!el) return '';
    var c = el.cloneNode(true);
    var junk = c.querySelectorAll('svg, .svg-wrapper');
    for (var i = 0; i < junk.length; i++) {
      if (junk[i].parentNode) junk[i].parentNode.removeChild(junk[i]);
    }
    return (c.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  /* Which navigation surface an element sits in. Order matters: mega menu
     content must be tested before the inline menu that contains it. */
  function surfaceOf(el) {
    /* UI-10: the collapsed bar's SHOP menu is a different surface again — a
       single column with flyouts. Distinguished so a click from the scrolled
       header is not confused with one from the full-width bar. */
    if (el.closest('.hcs-nav__flyout')) return 'shop_flyout';
    if (el.closest('.hcs-nav__panel--shop')) return 'shop_menu';
    /* HCS UI-8 dropdowns report as mega_menu so the numbers stay comparable
       with everything logged before the nav was rebuilt. */
    if (el.closest('.hcs-nav__panel')) return 'mega_menu';
    if (el.closest('.mega-menu__content')) return 'mega_menu';
    if (el.closest('.menu-drawer')) return 'drawer';
    if (el.closest('.header__inline-menu')) return 'header_top';
    if (el.closest('.footer__content-top, .footer')) return 'footer';
    return 'other';
  }

  /* Ancestor <details> summaries, outermost first. Skips the drawer's own
     container (its summary is the hamburger icon and has no text). */
  function ancestorLabels(el) {
    var out = [];
    var node = el.parentElement;
    while (node && node !== document.body) {
      if (node.tagName === 'DETAILS' && node.id !== 'Details-menu-drawer-container') {
        var s = node.querySelector(':scope > summary');
        var t = labelOf(s);
        if (t) out.unshift(t);
      }
      /* HCS UI-8 nav: no <details>, so the collection heading is the ancestor
         label. Skipped when the clicked link IS that heading. */
      if (node.classList && node.classList.contains('hcs-nav__item')) {
        var h = node.querySelector(':scope > .hcs-nav__link');
        if (h && h !== el) {
          var ht = labelOf(h);
          if (ht) out.unshift(ht);
        }
      }
      /* UI-10: a flyout link's parent is the collection row in the SHOP menu */
      if (node.classList && node.classList.contains('hcs-nav__shop-row')) {
        var sh = node.querySelector(':scope > .hcs-nav__shop-link');
        if (sh && sh !== el && !sh.contains(el)) {
          var st = labelOf(sh);
          if (st) out.unshift(st);
        }
      }
      /* a collection that has been pushed into the "More" dropdown */
      if (node.classList && node.classList.contains('hcs-nav__more-group')) {
        var mh = node.querySelector(':scope > .hcs-nav__more-head');
        if (mh && mh !== el) {
          var mt = labelOf(mh);
          if (mt) out.unshift(mt);
        }
      }
      node = node.parentElement;
    }
    return out;
  }

  /* 1-based index of the link's <li> among its siblings. */
  function positionOf(el) {
    var li = el.closest('li');
    if (!li || !li.parentElement) return 0;
    var sibs = li.parentElement.children;
    for (var i = 0; i < sibs.length; i++) {
      if (sibs[i] === li) return i + 1;
    }
    return 0;
  }

  function collectionHandle() {
    var b = document.body;
    var m = (b && b.className || '').match(/template-collection/);
    var el = document.querySelector('[data-hcs-collection]');
    if (el) return el.getAttribute('data-hcs-collection') || '';
    if (!m) return '';
    var path = (window.location.pathname || '').split('/');
    var i = path.indexOf('collections');
    return i > -1 && path[i + 1] ? path[i + 1] : '';
  }

  function hasFilters() {
    return /[?&](filter\.|sort_by=)/.test(window.location.search) ? 'yes' : 'no';
  }

  /* --- navigation --------------------------------------------------------- */

  /* HCS UI-8 nav opens on :hover with no <details> to fire `toggle`, so the
     open event is raised from pointer/focus instead. Held for 180ms so that
     sweeping the pointer across the bar does not log every collection. */
  function bindHcsNavOpens() {
    bindShopMenuOpens();
    var nodes = document.querySelectorAll('.hcs-nav__item--has-panel');
    for (var i = 0; i < nodes.length; i++) {
      (function (li) {
        if (li.__hcsNavBound) return;
        li.__hcsNavBound = true;

        var timer = null;
        var logged = false;

        function fire() {
          timer = null;
          if (logged) return; /* one event per open, not per link focused */
          logged = true;
          var head = li.querySelector(':scope > .hcs-nav__link');
          var own = labelOf(head);
          if (!own) return;
          clarityTag('nav_view', navView());
          hcsTrackEvent('hcs_nav_open', {
            nav_l1: own,
            nav_l2: '',
            nav_path: own,
            nav_depth: 1,
            nav_surface: 'mega_menu'
          });
        }

        li.addEventListener(
          'pointerenter',
          safe(function (e) {
            if (e.pointerType === 'touch') return; /* the click handler covers taps */
            if (timer) return;
            timer = window.setTimeout(safe(fire), 180);
          })
        );

        li.addEventListener(
          'pointerleave',
          safe(function () {
            if (timer) window.clearTimeout(timer);
            timer = null;
            logged = false;
          })
        );

        li.addEventListener('focusin', safe(fire));
        li.addEventListener(
          'focusout',
          safe(function (e) {
            if (!li.contains(e.relatedTarget)) logged = false;
          })
        );
      })(nodes[i]);
    }
  }

  /* UI-10: the collapsed bar's SHOP menu. Its own open event, plus one per
     flyout, so we can see which collections get explored from the scrolled
     header versus the full one. Same 180ms hold as the bar, so sweeping down
     the column does not log every row. */
  function bindShopMenuOpens() {
    var rows = document.querySelectorAll('.hcs-nav__shop-row--has-sub');
    for (var i = 0; i < rows.length; i++) {
      (function (row) {
        if (row.__hcsShopBound) return;
        row.__hcsShopBound = true;

        var timer = null;
        var logged = false;

        function fire() {
          timer = null;
          if (logged) return;
          logged = true;
          var own = row.getAttribute('data-full-label') || labelOf(row.querySelector('.hcs-nav__shop-link'));
          if (!own) return;
          clarityTag('nav_view', navView());
          hcsTrackEvent('hcs_nav_open', {
            nav_l1: 'Shop',
            nav_l2: own,
            nav_path: 'Shop > ' + own,
            nav_depth: 2,
            nav_surface: 'shop_menu'
          });
        }

        row.addEventListener(
          'pointerenter',
          safe(function (e) {
            if (e.pointerType === 'touch') return;
            if (timer) return;
            timer = window.setTimeout(safe(fire), 180);
          })
        );
        row.addEventListener(
          'pointerleave',
          safe(function () {
            if (timer) window.clearTimeout(timer);
            timer = null;
            logged = false;
          })
        );
        row.addEventListener('focusin', safe(fire));
      })(rows[i]);
    }
  }

  /* Menu opened. <details> fires `toggle` for click, keyboard and Dawn's
     hover-open alike, so one listener covers every way in. Bound per-element
     because `toggle` does not bubble. */
  function bindMenuOpens() {
    bindHcsNavOpens();
    var nodes = document.querySelectorAll(
      '.header__inline-menu details.mega-menu, .menu-drawer details, #Details-menu-drawer-container'
    );
    for (var i = 0; i < nodes.length; i++) {
      (function (d) {
        if (d.__hcsBound) return;
        d.__hcsBound = true;
        d.addEventListener(
          'toggle',
          safe(function () {
            if (!d.open) return;
            var isDrawerRoot = d.id === 'Details-menu-drawer-container';
            var surface = isDrawerRoot ? 'drawer' : surfaceOf(d);
            var own = labelOf(d.querySelector(':scope > summary'));
            var path = ancestorLabels(d);
            if (own) path.push(own);
            hcsTrackEvent('hcs_nav_open', {
              nav_l1: path[0] || (isDrawerRoot ? '(drawer root)' : ''),
              nav_l2: path[1] || '',
              nav_path: path.join(' > '),
              nav_depth: isDrawerRoot ? 0 : path.length,
              nav_surface: surface
            });
          })
        );
      })(nodes[i]);
    }
  }

  document.addEventListener(
    'click',
    safe(function (e) {
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;

      var surface = surfaceOf(a);
      if (surface === 'other') return;

      var path = ancestorLabels(a);
      var own = labelOf(a);
      var depth;

      if (a.closest('.hcs-nav')) {
        /* UI-8 nav: ancestorLabels() already carries the collection heading
           (and "More" when the collection was pushed into the overflow), so
           the depth is just how far down that path the link sits. */
        depth = path.length + 1;
      } else if (surface === 'mega_menu') {
        /* L2 links are flagged by class; anything deeper is an L3 link
           sitting in the nested ul.list-unstyled under its L2 anchor. */
        if (a.classList.contains('mega-menu__link--level-2')) {
          depth = 2;
        } else {
          depth = 3;
          var nested = a.closest('ul.list-unstyled');
          var parentLi = nested && nested.closest('li');
          var l2 = parentLi && parentLi.querySelector(':scope > a');
          if (l2) path.push(labelOf(l2));
        }
      } else if (surface === 'drawer') {
        depth = path.length + 1;
      } else {
        depth = 1;
      }

      var full = path.concat([own]);
      hcsTrackEvent('hcs_nav_click', {
        nav_l1: full[0] || '',
        nav_l2: full[1] || '',
        nav_l3: full[2] || '',
        nav_path: full.join(' > '),
        nav_depth: depth,
        nav_surface: surface,
        nav_position: positionOf(a)
      });
    }),
    true
  );

  /* --- collection / subcategory pages ------------------------------------- */

  function initCollection() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;

    var handle = collectionHandle();
    var count = grid.querySelectorAll('li.grid__item').length;
    var isEmpty = grid.classList.contains('collection--empty');

    hcsTrackEvent('hcs_collection_view', {
      collection_handle: handle,
      product_count: count,
      has_filters: hasFilters()
    });

    if (isEmpty || count === 0) {
      hcsTrackEvent('hcs_collection_empty', {
        collection_handle: handle,
        has_filters: hasFilters()
      });
    }

    /* Product card clicks. Delegated on document rather than on the grid
       because Dawn replaces the whole grid via AJAX when a facet changes. */
    document.addEventListener(
      'click',
      safe(function (e) {
        var a = e.target && e.target.closest && e.target.closest('a[href]');
        if (!a) return;
        var li = a.closest('li.grid__item[data-hcs-card-pos]');
        if (!li || !li.closest('#product-grid')) return;
        hcsTrackEvent('hcs_card_click', {
          collection_handle: handle,
          card_position: parseInt(li.getAttribute('data-hcs-card-pos'), 10) || 0,
          product_handle: li.getAttribute('data-hcs-handle') || '',
          product_available: li.getAttribute('data-hcs-available') || '',
          has_filters: hasFilters()
        });
      }),
      true
    );

    /* Filters and sort. Dawn's facet inputs are checkboxes and range fields
       inside .facets; sort is a <select>. One delegated change listener
       survives the AJAX re-render. */
    document.addEventListener(
      'change',
      safe(function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        if (t.id === 'SortBy' || t.name === 'sort_by') {
          hcsTrackEvent('hcs_facet_apply', {
            collection_handle: handle,
            facet_type: 'sort',
            facet_name: 'sort_by',
            facet_value: t.value || ''
          });
          return;
        }

        var facetForm = t.closest('.facets, facet-filters-form, #FacetFiltersForm, .mobile-facets');
        if (!facetForm) return;
        if (t.type === 'checkbox' && !t.checked) return;

        hcsTrackEvent('hcs_facet_apply', {
          collection_handle: handle,
          facet_type: 'filter',
          facet_name: (t.name || '').replace(/^filter\./, '').slice(0, 100),
          facet_value: (t.value || '').slice(0, 100)
        });
      }),
      true
    );

    /* Scroll depth — tells us whether anyone gets past the first rows. */
    var seen = {};
    var ticking = false;
    function measure() {
      ticking = false;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = ((window.pageYOffset || doc.scrollTop) / scrollable) * 100;
      var marks = [25, 50, 75, 100];
      for (var i = 0; i < marks.length; i++) {
        if (pct >= marks[i] && !seen[marks[i]]) {
          seen[marks[i]] = true;
          hcsTrackEvent('hcs_collection_scroll', {
            collection_handle: handle,
            scroll_depth: marks[i],
            product_count: count
          });
        }
      }
    }
    /* Throttle with setTimeout, NOT requestAnimationFrame. rAF is suspended
       while the tab is in the background, so a scroll there would set the
       ticking flag and never clear it - killing depth tracking for the rest
       of the page view even after the user comes back to the tab. */
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        setTimeout(measure, 200);
      },
      { passive: true }
    );
    /* Catch the case where the page loads already scrolled (a back-navigation
       restoring position, or a #anchor landing). */
    setTimeout(measure, 1000);
  }

  /* --- boot --------------------------------------------------------------- */

  function boot() {
    try {
      bindMenuOpens();
    } catch (e) {}
    try {
      initCollection();
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Dawn re-renders header and grid sections via the Section Rendering API.
     Re-bind the per-element toggle listeners when that happens; the click and
     change listeners are delegated on document and need no re-binding. */
  document.addEventListener(
    'shopify:section:load',
    safe(function () {
      bindMenuOpens();
    })
  );
})();
